import { useEffect, useRef, useState } from 'react';
import { aggregateInsights, fetchAllMetaPages, fetchMetaInsights } from '../../services/meta-api.js';
import { previousComparableRange } from './meta-period.js';

const clp = (x, currency) => new Intl.NumberFormat('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 }).format(x);
const number = (x) => Number(x || 0).toLocaleString('es-CL');
const decimal = (x) => Number(x || 0).toLocaleString('es-CL', { maximumFractionDigits: 1 });

/** Una sola lectura bajo demanda: comparación del total de campañas en Meta. */
export default function MetaPeriodComparison({ range, currency, campaignInsights }) {
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const seq = useRef(0);
  useEffect(() => {
    seq.current += 1;
    setPrevious(null);
    setError('');
    setLoading(false);
  }, [range.dateFrom, range.dateTo]);

  async function compare() {
    const dates = previousComparableRange(range);
    if (!dates) { setError('Elegí un rango de fechas válido.'); return; }
    const id = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const rows = await fetchAllMetaPages(fetchMetaInsights, { level: 'campaign', since: dates.since, until: dates.until }, 100);
      if (id === seq.current) setPrevious({ dates, metrics: aggregateInsights(rows) });
    } catch (e) {
      if (id === seq.current) setError(e.message || 'No se pudo obtener el período anterior.');
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }

  const current = aggregateInsights(campaignInsights);
  const values = previous && [
    { key: 'spend', label: 'Inversión', format: (x) => clp(x, currency), inverse: true },
    { key: 'impressions', label: 'Impresiones', format: number },
    { key: 'clicks', label: 'Clics generales', format: number },
    { key: 'leads', label: 'Leads atribuidos por Meta', format: number },
    { key: 'ctr', label: 'CTR general', format: (x) => decimal(x) + '%' },
    { key: 'cpl', label: 'Costo por lead', format: (x) => x ? clp(x, currency) : '—', inverse: true }
  ];
  return <section className="mgw-panel" aria-label="Comparación entre períodos de Meta">
    <div className="mgw-head"><div><p>Comparación · Meta Ads</p><h2>Período anterior equivalente</h2></div><span>Solo métricas publicitarias</span></div>
    <div className="mgw-panel-toolbar">
      <p>Consultá el período anterior cuando lo necesites; no se generan lecturas adicionales al cambiar los filtros.</p>
      <button type="button" className="mgw-btn is-secondary" disabled={loading} onClick={compare}>
        {loading ? 'Comparando…' : previous ? 'Actualizar comparación' : 'Comparar períodos'}
      </button>
    </div>
    {error ? <p className="mgw-alert" role="alert">{error}</p> : null}
    {previous ? <>
      <p className="mgw-muted">Período anterior: {previous.dates.since} al {previous.dates.until}. Las tasas y costos provienen de cada período, no se suman porcentajes.</p>
      <div className="mgw-table-wrap"><table className="mgw-table">
        <thead><tr><th>Métrica</th><th>Actual</th><th>Anterior</th><th>Variación</th></tr></thead>
        <tbody>{values.map((item) => {
          const a = current[item.key], b = previous.metrics[item.key];
          const delta = b > 0 ? (a - b) / b * 100 : null;
          return <tr key={item.key}><td>{item.label}</td><td>{item.format(a)}</td><td>{item.format(b)}</td>
            <td>{delta === null ? 'Sin base comparable' : (delta > 0 ? '+' : '') + decimal(delta) + '%'}
              {delta !== null ? <small style={{ display: 'block' }}>{item.inverse ? 'Bajar suele ser favorable, según objetivo' : 'Interpretar según objetivo de campaña'}</small> : null}
            </td></tr>;
        })}</tbody>
      </table></div>
    </> : <p className="mgw-muted">La comparación todavía no se consultó.</p>}
  </section>;
}
